import { Modal, Button } from 'antd'
import { PrinterOutlined } from '@ant-design/icons'
import { useEffect, useState, useRef } from 'react'
import QRCode from 'qrcode'

interface OrderItem {
  id: number
  product_name: string
  product_code?: string
  quantity: string
  unit: string
  unit_price: string
  total_price: string
}

interface DeliveryTask {
  id: number
  task_no: string
  order_no: string
  driver_name: string
  driver_phone: string
  status: number
  delivery_time: string
  completed_time: string
  remark: string
  verification_code?: string
  order?: {
    order_no: string
    total_amount?: string
    final_amount?: string
    delivery_fee?: number
    verification_code?: string
    created_at?: string
    remark?: string
    items?: OrderItem[]
    deliveryAddress?: {
      address: string
      contact_name: string
      contact_phone: string
    }
  }
}

interface Props {
  task: DeliveryTask
  visible: boolean
  onClose: () => void
}

export default function PrintDeliverySheet({ task, visible, onClose }: Props) {
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const printRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (visible && task) {
      const verificationCode = task.order?.verification_code || task.verification_code || ''
      const qrContent = JSON.stringify({
        type: 'delivery_verify',
        task_id: task.id,
        order_no: task.order?.order_no || task.order_no,
        code: verificationCode
      })

      QRCode.toDataURL(qrContent, {
        width: 150,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      }).then(url => setQrDataUrl(url)).catch(() => {})
    }
  }, [visible, task])

  const address = task.order?.deliveryAddress
  const items = task.order?.items || []
  const totalAmount = task.order?.final_amount || '0'
  const deliveryFee = Number(task.order?.delivery_fee || 0)
  const itemsTotal = items.reduce((sum: number, item: OrderItem) => sum + Number(item.total_price || 0), 0)
  const verificationCode = task.order?.verification_code || task.verification_code || ''

  const handlePrint = () => {
    const printContent = printRef.current
    if (!printContent) return
    const printWindow = window.open('', '_blank')
    if (!printWindow) { alert('请允许弹出窗口以打印配送单'); return }
    printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>配送单</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Microsoft YaHei', sans-serif; font-size: 12px; line-height: 1.5; color: #333; padding: 8px; width: 80mm; }
  .header { text-align: center; border-bottom: 2px dashed #333; padding-bottom: 8px; margin-bottom: 8px; }
  .header h2 { font-size: 16px; font-weight: bold; }
  .section { margin-bottom: 8px; border-bottom: 1px dashed #ccc; padding-bottom: 8px; }
  .section-title { font-weight: bold; font-size: 13px; margin-bottom: 4px; border-left: 3px solid #333; padding-left: 6px; }
  .info-row { display: flex; justify-content: space-between; margin-bottom: 2px; }
  .info-label { color: #666; }
  .info-value { font-weight: 500; text-align: right; flex: 1; margin-left: 8px; }
  .address { word-break: break-all; margin-top: 4px; padding: 4px; background: #f9f9f9; border-radius: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { text-align: left; padding: 4px 2px; border-bottom: 1px solid #eee; }
  th { font-weight: bold; border-bottom: 1px solid #333; }
  .text-right { text-align: right; }
  .text-center { text-align: center; }
  .total { margin-top: 8px; padding-top: 8px; border-top: 2px solid #333; text-align: right; font-size: 14px; font-weight: bold; }
  .footer { margin-top: 16px; text-align: center; border-top: 1px dashed #ccc; padding-top: 8px; }
  .qr-code { width: 120px; height: 120px; margin: 8px auto; display: block; }
  .verify-code { font-size: 14px; font-weight: bold; color: #ff4d4f; text-align: center; margin: 8px 0; padding: 4px; border: 1px dashed #ff4d4f; border-radius: 4px; }
</style></head><body>${printContent.innerHTML}</body></html>`)
    printWindow.document.close()
    setTimeout(() => printWindow.print(), 500)
  }

  return (
    <Modal
      title="配送单"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>关闭</Button>,
        <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
          打印
        </Button>
      ]}
      width={480}
    >
      <div ref={printRef} style={{ background: '#fff', padding: 16 }}>
        {/* 标题 */}
        <div style={{ textAlign: 'center', borderBottom: '2px dashed #333', paddingBottom: 8, marginBottom: 12 }}>
          <h2 style={{ fontSize: 18, fontWeight: 'bold' }}>智厨云 配送单</h2>
          <div style={{ fontSize: 11, color: '#666' }}>任务号: {task.task_no || task.id}</div>
        </div>

        {/* 订单信息 */}
        <div style={{ marginBottom: 10, borderBottom: '1px dashed #ccc', paddingBottom: 8 }}>
          <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4, borderLeft: '3px solid #333', paddingLeft: 6 }}>订单信息</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>订单号:</span><span style={{ fontWeight: 500 }}>{task.order?.order_no || '-'}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>下单时间:</span><span style={{ fontWeight: 500 }}>{task.order?.created_at ? task.order.created_at.substring(0, 16).replace('T', ' ') : '-'}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>配送时间:</span><span style={{ fontWeight: 500 }}>{task.delivery_time ? task.delivery_time.substring(0, 16).replace('T', ' ') : '-'}</span></div>
          {task.order?.remark && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>订单备注:</span><span style={{ fontWeight: 500 }}>{task.order.remark}</span></div>}
        </div>

        {/* 收货信息 */}
        <div style={{ marginBottom: 10, borderBottom: '1px dashed #ccc', paddingBottom: 8 }}>
          <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4, borderLeft: '3px solid #333', paddingLeft: 6 }}>收货信息</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>收货人:</span><span style={{ fontWeight: 500 }}>{address?.contact_name || '-'}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>电话:</span><span style={{ fontWeight: 500 }}>{address?.contact_phone || '-'}</span></div>
          <div style={{ wordBreak: 'break-all', marginTop: 4, padding: 4, background: '#f9f9f9', borderRadius: 4 }}>
            <span style={{ color: '#666' }}>地址:</span> {address?.address || '-'}
          </div>
        </div>

        {/* 商品清单 */}
        <div style={{ marginBottom: 10, borderBottom: '1px dashed #ccc', paddingBottom: 8 }}>
          <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4, borderLeft: '3px solid #333', paddingLeft: 6 }}>商品清单</div>
          {items.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #333' }}>
                  <th style={{ textAlign: 'left', padding: '4px 2px' }}>品名</th>
                  <th style={{ textAlign: 'center', padding: '4px 2px' }}>数量</th>
                  <th style={{ textAlign: 'right', padding: '4px 2px' }}>单价</th>
                  <th style={{ textAlign: 'right', padding: '4px 2px' }}>小计</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={item.id || idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '4px 2px' }}>{item.product_name}</td>
                    <td style={{ textAlign: 'center', padding: '4px 2px' }}>{item.quantity}{item.unit}</td>
                    <td style={{ textAlign: 'right', padding: '4px 2px' }}>¥{item.unit_price}</td>
                    <td style={{ textAlign: 'right', padding: '4px 2px' }}>¥{item.total_price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div style={{ color: '#999', textAlign: 'center', padding: 8 }}>暂无商品信息</div>}
          {deliveryFee > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: '#666' }}>
              <span>配送费</span><span>¥{deliveryFee.toFixed(2)}</span>
            </div>
          )}
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '2px solid #333', textAlign: 'right', fontSize: 15, fontWeight: 'bold' }}>
            合计: ¥{Number(totalAmount).toFixed(2)}
            {deliveryFee > 0 && <div style={{ fontSize: 10, fontWeight: 'normal', color: '#666', marginTop: 2 }}>(商品¥{itemsTotal.toFixed(2)} + 配送¥{deliveryFee.toFixed(2)})</div>}
          </div>
        </div>

        {/* 配送员信息 */}
        <div style={{ marginBottom: 10, borderBottom: '1px dashed #ccc', paddingBottom: 8 }}>
          <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4, borderLeft: '3px solid #333', paddingLeft: 6 }}>配送信息</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>配送员:</span><span style={{ fontWeight: 500 }}>{task.driver_name || '-'}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>配送电话:</span><span style={{ fontWeight: 500 }}>{task.driver_phone || '-'}</span></div>
          {task.remark && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#666' }}>备注:</span><span style={{ fontWeight: 500 }}>{task.remark}</span></div>}
        </div>

        {/* 二维码 + 核销（最下面） */}
        <div style={{ textAlign: 'center', marginTop: 12, borderTop: '1px dashed #ccc', paddingTop: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 8, borderLeft: '3px solid #333', paddingLeft: 6, textAlign: 'left' }}>确认收货</div>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>请使用小程序扫码确认收货</div>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="扫码确认收货" style={{ width: 120, height: 120, margin: '0 auto', display: 'block' }} />
          ) : (
            <div style={{ width: 120, height: 120, margin: '0 auto', border: '1px dashed #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 11 }}>加载中...</div>
          )}
          {verificationCode && (
            <div style={{ fontSize: 14, fontWeight: 'bold', color: '#ff4d4f', textAlign: 'center', margin: '8px 0', padding: 4, border: '1px dashed #ff4d4f', borderRadius: 4 }}>
              验证码: {verificationCode}
            </div>
          )}
          <div style={{ fontSize: 10, color: '#999', marginTop: 12 }}>智厨云 - 新鲜直达 | 感谢您的订购!</div>
        </div>
      </div>
    </Modal>
  )
}
